import { Alert, Button, Checkbox, Field, Input, Textarea } from "@norican/react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { ActionLog } from "@/components/action-log";
import { HelpTerm } from "@/components/help-term";
import { Notice, Page } from "@/components/page";
import { PersonPicker } from "@/components/person-picker";
import { STATUS_NOTE, StatusBadge } from "@/components/status";
import { control, subText } from "@/components/table";
import { useCreateOffboarding, useMe, useOffboarding, useOffboardingAction } from "@/lib/api";
import { mayRaise, READ_ONLY_NOTE } from "@/lib/capability";
import { BROWSER_TZ, errorMessage, inZoneSentence, zoneLabel } from "@/lib/format";

/**
 * Raise an offboarding, and revert one.
 *
 * Routed at `/leaver` (the form) and `/leaver/:id` (one record, with its log
 * and the revert/cancel controls). `/leaver/:id` is the only detail screen in
 * the app; the queue links here rather than showing its own copy.
 *
 * Two things this screen refuses to fake, because they are the ways an
 * offboarding tool lies to the person using it:
 *
 * 1. **Time.** HR types a wall-clock time and names the zone it belongs to.
 *    The confirmation restates that same local time ("17:00 Prague"), never a
 *    UTC instant, and the server does the conversion.
 * 2. **Execution.** No progress bar and no "account disabled" message: the
 *    record and its action log are the only things this screen claims.
 *
 *    What it deliberately does NOT say is how the backend is configured. An
 *    earlier version explained dry-run and scheduling in a callout on every
 *    save, and it confused the people it was meant to reassure — whether the
 *    worker is switched on is ours to manage and nothing HR can act on. That
 *    state lives on /admin now, in front of the two people who can change it.
 */

/** Zones Norican actually operates in, plus whatever this browser is set to. */
const ZONES = Array.from(
  new Set([
    BROWSER_TZ,
    "Europe/Berlin",
    "Europe/Paris",
    "Europe/Madrid",
    "Europe/Prague",
    "Europe/Copenhagen",
    "Asia/Kolkata",
    "America/Chicago",
    "UTC",
  ]),
);

function humanDate(dateValue: string, timeValue: string, zone: string): string {
  const parsed = new Date(`${dateValue}T${timeValue || "00:00"}:00`);
  if (Number.isNaN(parsed.getTime())) {
    return `${dateValue} ${timeValue} ${zoneLabel(zone)}`;
  }
  const day = new Intl.DateTimeFormat("en-GB", { dateStyle: "full" }).format(parsed);
  return `${day} at ${timeValue} ${zoneLabel(zone)}`;
}

export function LeaverPage() {
  const { id } = useParams<{ id?: string }>();
  return id ? <LeaverRecord id={id} /> : <NewOffboardingForm />;
}

// ─── Screen 1: the form ──────────────────────────────────────────────────────

type Timing = "now" | "scheduled";

function NewOffboardingForm() {
  const navigate = useNavigate();
  const createMutation = useCreateOffboarding();
  const me = useMe().data;

  const [subjectUpn, setSubjectUpn] = useState("");
  const [timing, setTiming] = useState<Timing>("scheduled");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("17:00");
  const [zone, setZone] = useState(BROWSER_TZ);
  const [delegate, setDelegate] = useState("");
  const [grantSendAs, setGrantSendAs] = useState(false); // off unless asked for
  // The OneDrive goes to the mailbox delegate unless HR says otherwise, which
  // is the common case; `onedriveEdited` is what stops the default from
  // overwriting a name they have deliberately typed.
  const [onedrive, setOnedrive] = useState("");
  const [onedriveEdited, setOnedriveEdited] = useState(false);
  const onedriveValue = onedriveEdited ? onedrive : delegate;
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);

  const submit = () =>
    createMutation.mutate(
      {
        subject_upn: subjectUpn.trim(),
        timing,
        // A naive wall-clock string on purpose: the server reads it in
        // local_tz. Sending a browser-computed UTC instant would silently use
        // the browser's zone instead of the one HR chose.
        effective_at: timing === "now" ? null : `${date}T${time || "00:00"}:00`,
        local_tz: zone,
        delegate_to_upn: delegate.trim() || null,
        grant_send_as: grantSendAs,
        onedrive_to_upn: onedriveValue.trim() || null,
        note: note.trim() || null,
      },
      {
        onSuccess: (result) => {
          if (result) {
            navigate(`/leaver/${result.offboarding.id}`);
          }
        },
      },
    );

  const missing: string[] = [];
  if (!subjectUpn.trim()) {
    missing.push("the leaver's UPN");
  }
  if (timing === "scheduled" && !date) {
    missing.push("a date");
  }
  if (timing === "scheduled" && !time) {
    missing.push("a time");
  }
  if (grantSendAs && !delegate.trim()) {
    missing.push("a delegate for Send As");
  }
  const ready = missing.length === 0;

  const whenSentence =
    timing === "now" ? "effective immediately" : `effective ${humanDate(date, time, zone)}`;

  return (
    <Page
      title="Raise an offboarding"
      intro="Name the leaver, when they go, and who takes their mailbox. You confirm it on the next step before anything is saved."
    >
      <div className="max-w-2xl">
        {!mayRaise(me) ? (
          <Alert tone="info" title="You cannot raise an offboarding" className="mt-6">
            {READ_ONLY_NOTE}{" "}
            <Link to="/help" className="underline">
              Who can do what
            </Link>
          </Alert>
        ) : confirming ? (
          <ConfirmStep
            subjectUpn={subjectUpn.trim()}
            whenSentence={whenSentence}
            zone={zone}
            delegate={delegate.trim()}
            grantSendAs={grantSendAs}
            onedrive={onedriveValue.trim()}
            note={note.trim()}
            submitting={createMutation.isPending}
            error={createMutation.isError ? errorMessage(createMutation.error) : null}
            onBack={() => setConfirming(false)}
            onConfirm={submit}
          />
        ) : (
          <form
            className="mt-6 space-y-5"
            onSubmit={(event) => {
              event.preventDefault();
              if (ready) {
                setConfirming(true);
              }
            }}
          >
            {/* Search by name, store the UPN: HR knows people by name and the
                tenant knows them by sign-in name, and the gap between the two
                is where the wrong Anna gets offboarded. */}
            <PersonPicker
              label="Who is leaving"
              required
              hint={
                <>
                  Type a name and pick the person. Their sign-in <HelpTerm id="upn">UPN</HelpTerm>{" "}
                  is what gets recorded.
                </>
              }
              placeholder="Surname, or firstname.lastname@noricangroup.com"
              value={subjectUpn}
              onChange={setSubjectUpn}
            />

            <fieldset className="space-y-2">
              <legend className="text-fg text-sm font-medium">When</legend>
              <div className="flex flex-wrap items-center gap-4">
                {(["now", "scheduled"] as const).map((option) => (
                  <label key={option} className="text-fg flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="timing"
                      value={option}
                      checked={timing === option}
                      onChange={() => setTiming(option)}
                      className="accent-fg"
                    />
                    {option === "now" ? "Now" : "At a date and time"}
                  </label>
                ))}
              </div>

              {timing === "scheduled" ? (
                <div className="flex flex-wrap items-end gap-3 pt-1">
                  <label className="text-fg-muted flex flex-col gap-1 text-xs">
                    Date
                    <input
                      type="date"
                      value={date}
                      onChange={(event) => setDate(event.target.value)}
                      className={control}
                    />
                  </label>
                  <label className="text-fg-muted flex flex-col gap-1 text-xs">
                    Time
                    <input
                      type="time"
                      value={time}
                      onChange={(event) => setTime(event.target.value)}
                      className={control}
                    />
                  </label>
                  <label className="text-fg-muted flex flex-col gap-1 text-xs">
                    Time zone
                    <select
                      value={zone}
                      onChange={(event) => setZone(event.target.value)}
                      className={control}
                    >
                      {ZONES.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : null}
              <p className={subText}>
                The time you type is read in the zone you pick, not in this browser&apos;s zone.
              </p>
            </fieldset>

            {/* The delegate is picked the same way: naming the wrong one hands
                a stranger somebody else's correspondence. */}
            <PersonPicker
              label="Who takes the mailbox"
              hint={
                <>
                  Optional. The <HelpTerm id="delegate">delegate</HelpTerm> gets Full Access to the
                  mailbox. Leave empty if nobody is taking it over.
                </>
              }
              placeholder="Surname, or colleague@noricangroup.com"
              value={delegate}
              onChange={setDelegate}
            />

            {/* Separate from the mailbox, because the person who answers the
                correspondence and the person who picks up the work are often
                not the same. Prefilled from the delegate so the common case
                stays one name. */}
            <PersonPicker
              label="Who takes the OneDrive"
              hint={
                <>
                  Optional, and read only. They see the leaver&apos;s files under Shared with me.
                  Defaults to whoever takes the mailbox. Note the files are deleted about 107 days
                  after the offboarding, once the licence is reclaimed, so anything worth keeping
                  has to be copied out.
                </>
              }
              placeholder="Surname, or colleague@noricangroup.com"
              value={onedriveValue}
              onChange={(v: string) => {
                setOnedriveEdited(true);
                setOnedrive(v);
              }}
            />

            <div>
              <Checkbox.Root
                checked={grantSendAs}
                onCheckedChange={(checked: boolean) => setGrantSendAs(checked)}
                disabled={!delegate.trim()}
                label="Also grant Send As (the delegate can send mail as the leaver)"
              />
              <p className={`${subText} mt-1`}>
                Off unless you turn it on. Without it the delegate can read the mailbox but sends
                under their own name. <HelpTerm id="send-as">What Send As means</HelpTerm>
              </p>
            </div>

            <Field label="Note" hint="Optional. Recorded on the log entry for this record.">
              <Textarea
                rows={2}
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder="Context a colleague would need in six months."
              />
            </Field>

            <div className="border-border flex items-center gap-3 border-t pt-4">
              <Button type="submit" disabled={!ready}>
                Review
              </Button>
              {!ready ? <span className={subText}>Still needs {missing.join(", ")}.</span> : null}
            </div>
          </form>
        )}
      </div>
    </Page>
  );
}

// ─── The confirm step ────────────────────────────────────────────────────────

interface ConfirmStepProps {
  subjectUpn: string;
  whenSentence: string;
  zone: string;
  delegate: string;
  grantSendAs: boolean;
  onedrive: string;
  note: string;
  submitting: boolean;
  error: string | null;
  onBack: () => void;
  onConfirm: () => void;
}

/**
 * Restates the form in plain words before anything is written.
 *
 * Deliberately worded in the future-conditional ("will be recorded", not "will
 * be disabled"), and carries the execution caveat inline rather than in a
 * footnote, because the sentence a user skims is the one that has to be true.
 */
function ConfirmStep({
  subjectUpn,
  whenSentence,
  zone,
  delegate,
  grantSendAs,
  onedrive,
  note,
  submitting,
  error,
  onBack,
  onConfirm,
}: ConfirmStepProps) {
  return (
    <section className="mt-6" aria-label="Confirm">
      <h2 className="text-fg text-sm font-semibold">Confirm this offboarding</h2>
      <div className="border-border text-fg mt-3 space-y-2 border-l-2 py-1 pl-4 text-sm">
        <p>
          <span className="font-medium">{subjectUpn}</span> is recorded as leaving, {whenSentence}.
        </p>
        <p>
          {delegate ? (
            <>
              <span className="font-medium">{delegate}</span> is recorded as taking the mailbox
              {grantSendAs
                ? ", with Send As — they will be able to send mail as the leaver."
                : ", without Send As — they read the mailbox but send under their own name."}
            </>
          ) : (
            "Nobody is recorded as taking the mailbox."
          )}
        </p>
        <p>
          {onedrive ? (
            <>
              <span className="font-medium">{onedrive}</span> gets read access to the OneDrive.
            </>
          ) : (
            "Nobody is recorded as taking the OneDrive."
          )}
        </p>
        {note ? <p className="text-fg-muted">Note: {note}</p> : null}
        <p className={subText}>
          The instant is stored with the zone ({zone}), so every screen shows this back to you as
          the local time you typed.
        </p>
      </div>

      {error ? (
        <Alert tone="danger" title="Not saved" className="mt-3">
          {error}
        </Alert>
      ) : null}

      <div className="border-border mt-4 flex items-center gap-3 border-t pt-4">
        <Button onClick={onConfirm} loading={submitting} disabled={submitting}>
          Save the record
        </Button>
        <Button variant="ghost" onClick={onBack} disabled={submitting}>
          Back to the form
        </Button>
      </div>
    </section>
  );
}

// ─── Screen 2: one record, with revert and cancel ────────────────────────────

function LeaverRecord({ id }: { id: string }) {
  const [pendingAction, setPendingAction] = useState<"revert" | "cancel" | "reinstate" | null>(
    null,
  );
  const [reason, setReason] = useState("");

  const recordQuery = useOffboarding(id);
  const actionMutation = useOffboardingAction(id);
  const me = useMe().data;

  if (recordQuery.isLoading) {
    return (
      <Page title="Leaver">
        <Notice>Loading the record…</Notice>
      </Page>
    );
  }
  if (recordQuery.error || !recordQuery.data) {
    return (
      <Page title="Leaver">
        <Notice tone="danger">
          {errorMessage(recordQuery.error, "That record could not be loaded.")}
        </Notice>
      </Page>
    );
  }

  const record = recordQuery.data;
  // `actions` and `risk_flags` carry server-side defaults, so the generated
  // types make them optional; an absent list means an empty one.
  const actions = record.actions ?? [];
  const riskFlags = record.risk_flags ?? [];
  // One question now: may this session use HROps at all. Whose record it is
  // does not enter into it — HR covers for each other, and the log names who
  // did what, which is the control that matters.
  const mayAct = mayRaise(me);
  const canRevert = mayAct && record.status !== "reverted" && record.status !== "cancelled";
  const canCancel = mayAct && record.status === "scheduled";
  // Reinstate is offered only where HROps actually disabled somebody. Mirrors
  // REINSTATABLE in routes_leaver.py: a record that never ran disabled nobody,
  // so there is nobody to put back.
  const canReinstate =
    mayAct && ["phase1_done", "partial", "completed"].includes(record.status ?? "");

  return (
    <Page
      title={record.subject_display_name ?? record.subject_upn}
      intro={
        <>
          Raised by {record.requester_upn} ({record.requester_role}) on{" "}
          {inZoneSentence(record.created_at, record.local_tz)}.{" "}
          <Link to="/queue" className="underline">
            Back to the queue
          </Link>
          .
        </>
      }
    >
      <div className="max-w-3xl">
        <dl className="border-border mt-5 grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2 border-t pt-4 text-sm">
          <dt className="text-fg-muted">Status</dt>
          <dd className="text-fg flex flex-wrap items-baseline gap-2">
            <StatusBadge status={record.status} />
            {STATUS_NOTE[record.status] ? (
              <span className={subText}>{STATUS_NOTE[record.status]}</span>
            ) : null}
          </dd>

          <dt className="text-fg-muted">Leaver</dt>
          <dd className="text-fg">{record.subject_upn}</dd>

          <dt className="text-fg-muted">Effective</dt>
          <dd className="text-fg">{inZoneSentence(record.effective_at, record.local_tz)}</dd>

          <dt className="text-fg-muted">Mailbox</dt>
          <dd className="text-fg">
            {record.delegate_to_upn
              ? `${record.delegate_to_upn}${record.grant_send_as ? " (with Send As)" : " (no Send As)"}`
              : "No handover recorded"}
          </dd>

          <dt className="text-fg-muted">OneDrive</dt>
          <dd className="text-fg">
            {record.onedrive_to_upn
              ? `${record.onedrive_to_upn} (read only)`
              : "No handover recorded"}
          </dd>

          <dt className="text-fg-muted">Phase 2 due</dt>
          <dd className="text-fg">
            {record.phase2_due_at
              ? inZoneSentence(record.phase2_due_at, record.local_tz)
              : "Not scheduled — the revert window stays open."}
          </dd>

          {record.reinstate_requested_at ? (
            <>
              <dt className="text-fg-muted">Re-enabled</dt>
              <dd className="text-fg">
                {record.reinstated_at
                  ? `${inZoneSentence(record.reinstated_at, record.local_tz)}`
                  : "Requested, not done yet"}
                {record.reinstate_requested_by
                  ? `, asked for by ${record.reinstate_requested_by}`
                  : ""}
                <span className={`${subText} block`}>{record.reinstate_reason}</span>
              </dd>
            </>
          ) : null}

          {record.reverted_at ? (
            <>
              <dt className="text-fg-muted">Reverted</dt>
              <dd className="text-fg">
                {inZoneSentence(record.reverted_at, record.local_tz)} by {record.reverted_by}
              </dd>
            </>
          ) : null}

          {record.cancelled_at ? (
            <>
              <dt className="text-fg-muted">Cancelled</dt>
              <dd className="text-fg">
                {inZoneSentence(record.cancelled_at, record.local_tz)} by {record.cancelled_by}
              </dd>
            </>
          ) : null}
        </dl>

        {riskFlags.length > 0 ? (
          <p className={`${subText} mt-4`}>
            {riskFlags.length} risk flag{riskFlags.length === 1 ? "" : "s"} recorded against this
            person.{" "}
            <Link to="/flags" className="underline">
              Risk flags
            </Link>
          </p>
        ) : null}

        {/* Revert / cancel */}
        <section className="border-border mt-6 border-t pt-4">
          {pendingAction ? (
            <div className="space-y-3">
              <p className="text-fg text-sm">
                {pendingAction === "revert"
                  ? "Reverting marks this record reverted and logs you as the person who decided. It does not switch the account back on — use Re-enable the account for that."
                  : pendingAction === "cancel"
                    ? "Cancelling stops this record before it runs and logs you as the person who decided."
                    : "This puts the person back: their account is enabled again in Entra and in on-prem AD. On-prem access returns within seconds, cloud access within about 15 minutes." +
                      (record.status === "completed"
                        ? " Their mailbox has already been converted to shared and the licence released — re-enabling the account does not undo either, so they will need a licence again before they can use mail."
                        : "")}
              </p>
              <Field label="Reason" required hint="Stored on the log entry. Required.">
                <Input
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={
                    pendingAction === "revert"
                      ? "Wrong person named"
                      : pendingAction === "cancel"
                        ? "Resignation withdrawn"
                        : "Resignation withdrawn, back at their desk on Monday"
                  }
                />
              </Field>
              {actionMutation.isError ? (
                <Alert tone="danger" title="Not applied">
                  {errorMessage(actionMutation.error)}
                </Alert>
              ) : null}
              <div className="flex items-center gap-3">
                <Button
                  tone={pendingAction === "revert" ? "danger" : "neutral"}
                  loading={actionMutation.isPending}
                  disabled={!reason.trim() || actionMutation.isPending}
                  onClick={() =>
                    actionMutation.mutate(
                      { action: pendingAction, reason: reason.trim() },
                      {
                        onSuccess: () => {
                          setPendingAction(null);
                          setReason("");
                        },
                      },
                    )
                  }
                >
                  {pendingAction === "revert"
                    ? "Revert this offboarding"
                    : pendingAction === "cancel"
                      ? "Cancel this offboarding"
                      : "Put this person back"}
                </Button>
                <Button
                  variant="ghost"
                  disabled={actionMutation.isPending}
                  onClick={() => {
                    setPendingAction(null);
                    setReason("");
                    actionMutation.reset();
                  }}
                >
                  Keep it
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                tone="danger"
                disabled={!canRevert}
                onClick={() => setPendingAction("revert")}
              >
                Revert
              </Button>
              <Button
                variant="outline"
                disabled={!canCancel}
                onClick={() => setPendingAction("cancel")}
              >
                Cancel
              </Button>
              {/* Only rendered where it means something. A disabled
                  "Re-enable" on a record that never ran would invite the
                  question this app should never raise: can I switch anyone
                  back on from here? */}
              {canReinstate ? (
                <Button variant="outline" onClick={() => setPendingAction("reinstate")}>
                  Re-enable the account
                </Button>
              ) : null}
              <span className={subText}>
                {!mayAct
                  ? READ_ONLY_NOTE
                  : canRevert
                    ? "HR owns wrongful offboardings: revert is yours to use, and it is logged."
                    : `Already ${record.status}.`}
              </span>
            </div>
          )}
        </section>

        {/* The log */}
        <section className="mt-8">
          <h2 className="text-fg text-sm font-semibold">Action log</h2>
          <p className={`${subText} mt-1`}>
            Append-only, oldest first. Every entry names both the person and the application, which
            is the part Entra&apos;s own audit log cannot show.
          </p>
          <ActionLog entries={actions} tz={record.local_tz} />
        </section>
      </div>
    </Page>
  );
}
